import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import * as XLSX from 'xlsx-js-style';
import "../css/others/Initialsetup.css";
import "../css/others/Table.css";
import { useNavigate } from "react-router-dom";
import { apiCall, s3AxiosInstance } from "../main/ApiCall";
import axios from "axios";
import ToastPopup from "../popups/ToastPopup";
import ConfirmationPopup from "../popups/ConfirmationPopup";
import { UploadedData, FieldMapping, createToastMessage, ToastMessage } from '../interFaces/interfaces';
import EncryptionUtils from "../main/encryptionUtils";
const useFileUpload = (file: File | null, setToastMessage: (msg: ToastMessage | null) => void) => {
  const [uploadedData, setUploadedData] = useState<UploadedData[]>([]);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const uploadFileToS3 = async () => {
    if (!file) return;
    try {
      const presignedUrl = await apiCall("get", "/api/bulk-upload/s3presigneduploadurl", null, { fileName: file.name, action: "bulk_upload" });
      if (!presignedUrl) throw new Error("Failed to retrieve presigned URL");
      await s3AxiosInstance.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
      setToastMessage(createToastMessage("File uploaded successfully", "success", 3000));
      const reader = new FileReader();
      if (file.type === "text/csv") {
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const lines = content.split("\n").map((line) => line.split(","));
          const fileHeaders = lines[0].map((header) => header.trim());
          setFileHeaders(fileHeaders);
          const data = lines.slice(1).map((line) => fileHeaders.reduce((row, header, idx) => { row[header] = line[idx]?.trim() || ""; return row; }, {} as UploadedData));
          setUploadedData(data);
          setFileUploaded(true);
        };
        reader.readAsText(file);
      } else if (
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        reader.onload = (e) => {
          const workbook = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheetData = XLSX.utils.sheet_to_json<any[]>(workbook.Sheets[sheetName], { header: 1 });
          const fileHeaders = sheetData[0] as string[];
          setFileHeaders(fileHeaders);
          const data = sheetData.slice(1).map((row: any[]) =>
            fileHeaders.reduce((rowData, header, idx) => { rowData[header] = row[idx] || ""; return rowData }, {} as UploadedData)
          );
          setUploadedData(data);
          setFileUploaded(true);
        };
        reader.readAsArrayBuffer(file);
      } else if (file.type === "application/json") {
        reader.onload = (e) => {
          const data = JSON.parse(e.target?.result as string);
          if (Array.isArray(data)) {
            setFileHeaders(Object.keys(data[0]));
            setUploadedData(data);
            setFileUploaded(true);
          } else {
            setToastMessage(createToastMessage("Invalid JSON structure.", "warning", 3000));
          }
        };
        reader.readAsText(file);
      } else {
        setToastMessage(createToastMessage("Unsupported file type.", "warning", 3000));
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error uploading file";
      setToastMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  return { uploadedData, fileHeaders, fileUploaded, uploadFileToS3 };
};
const findBestMatch = (target: string, options: string[]): string | null => {
  if (!options.length) return null;
  const targetLower = target.toLowerCase().trim();
  const exactMatch = options.find(opt => opt.toLowerCase().trim() === targetLower);
  if (exactMatch) return exactMatch;
  const containsMatch = options.find(opt =>
    opt.toLowerCase().includes(targetLower) || targetLower.includes(opt.toLowerCase())
  );
  if (containsMatch) return containsMatch;
  const cleanTarget = targetLower.replace(/[^a-z0-9]/g, '');
  const cleanOptionsMatch = options.find(opt => {
    const cleanOpt = opt.toLowerCase().replace(/[^a-z0-9]/g, '');
    return cleanOpt === cleanTarget || cleanTarget.includes(cleanOpt) || cleanOpt.includes(cleanTarget);
  });
  if (cleanOptionsMatch) return cleanOptionsMatch;
  return null;
};
const InitialSetup: React.FC = () => {
  const [mapping, setMapping] = useState<FieldMapping>({});
  const [productAdminFields, setProductAdminFields] = useState<string[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [fieldsFetched, setFieldsFetched] = useState<boolean>(false);
  const [clientIdSubmitted, setClientIdSubmitted] = useState<boolean>(false);
  const [, setIsError] = useState<boolean>(false);
  const [showSaveWarning, setShowSaveWarning] = useState<boolean>(false);
  const [hasExistingMappings, setHasExistingMappings] = useState<boolean>(false);
  const [existingMappedData, setExistingMappedData] = useState<FieldMapping>({});
  const [, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [fields, setFields] = useState<any>({});
  const [allFileHeaders, setAllFileHeaders] = useState<string[]>([]);
  const [showUploadButton, setShowUploadButton] = useState<boolean>(false);
  const [autoMappedFields, setAutoMappedFields] = useState<Set<string>>(new Set());
  const mandatoryFields = useMemo(() => ["Project Category", "Project ID", "Project Name", "Account ID", "Account Name", "Business Unit", "Status", "Team Size", "Delivery Model", "Billing Type", "Project Manager Name", "Project Manager Email", "Delivery Manager Name", "Delivery Manager Email", "Delivery Head Name", "Delivery Head Email", "Quality Consultant Name", "Quality Consultant Email", "Quality Manager Name", "Quality Manager Email", "Test Manager Name", "Test Manager Email", "Technical Lead Name", "Technical Lead Email", "Customer Location", "Project Location", "Project Life Cycle", "Project Start Date", "Project End Date"], []);
  const { fileHeaders, fileUploaded, uploadFileToS3 } = useFileUpload(file, setMessage);
  const storedClientId = localStorage.getItem("clientId");
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions?.tabs?.["projects"]) {
      setFields(permissions.tabs["projects"].fields);
    } else {
      console.log("No permissions found for 'projects' tab");
    }
  }, []);
  useEffect(() => {
    if (fileHeaders.length > 0) {
      setAllFileHeaders(fileHeaders);
    }
  }, [fileHeaders]);
  useEffect(() => {
    const checkExistingMappings = async () => {
      try {
        if (storedClientId) {
          setClientId(storedClientId);
          const response = await apiCall("get", "/api/client-mappings", null, { clientId: storedClientId });
          if (response && response.mappedData && Object.keys(response.mappedData).length > 0) {
            setHasExistingMappings(true);
            const labelMappedData: FieldMapping = {};
            const rawData = await apiCall("get", "/api/client-apis", null, { clientId: storedClientId });
            const labelToAttribute = rawData.attributeToLabelMapping;
            const attributeToLabel = Object.fromEntries(
              Object.entries(labelToAttribute).map(([label, attribute]) => [attribute, label])
            );
            for (const [attribute, mappedValue] of Object.entries(response.mappedData)) {
              const label = attributeToLabel[attribute];
              if (label) {
                labelMappedData[label] = mappedValue as string | null;
              }
            }
            setExistingMappedData(labelMappedData);
          }
        }
      } catch (error) {
        console.error("Error checking existing mappings:", error);
      } finally {
        setIsLoading(false);
      }
    };
    checkExistingMappings();
  }, [storedClientId]);
  useEffect(() => {
    if (fileUploaded) {
      if (storedClientId) {
        setClientId(storedClientId);
        setClientIdSubmitted(true);
      } else {
        setMessage(createToastMessage("Client ID not found in local storage. Please log in again.", "warning", 3000));
      }
    }
  }, [fileUploaded, storedClientId]);
  useEffect(() => {
    const fetchFields = async () => {
      if (!clientId) return;
      try {
        const response = await apiCall("get", "/api/client-apis", null, { clientId });
        const fieldsObject = response.attributeToLabelMapping;
        const excludedFields = ["to be audited in current month", "remarks by qc", "audit type", "auditor name", "auditor employee id", "audit team", "audit scope", "audit objective", "current month audit type", "remarks by audit team"];
        const filteredFields = Object.keys(fieldsObject).filter((field) => !excludedFields.includes(field.toLowerCase()));
        setProductAdminFields(filteredFields);
        setFieldsFetched(true);
      } catch (error) {
        setMessage(createToastMessage("Failed to fetch product admin fields.", "error", 3000));
        setIsError(true);
      }
    };
    if (clientIdSubmitted && !hasExistingMappings) {
      fetchFields();
    }
  }, [clientIdSubmitted, clientId, hasExistingMappings]);
  const performAutoMapping = useCallback(() => {
    if (allFileHeaders.length === 0 || productAdminFields.length === 0) return;
    const newMapping: FieldMapping = {};
    let autoMapped = 0;
    const autoMappedSet = new Set<string>();
    const allFieldsToMap = [
      ...mandatoryFields.filter(field => productAdminFields.includes(field)),
      ...productAdminFields.filter(field => !mandatoryFields.includes(field))
    ];
    allFieldsToMap.forEach(field => {
      const bestMatch = findBestMatch(field, allFileHeaders);
      if (bestMatch) {
        newMapping[field] = bestMatch;
        autoMapped++;
        autoMappedSet.add(field);
      } else {
        newMapping[field] = null;
      }
    });
    setMapping(newMapping);
    setAutoMappedFields(autoMappedSet);
    if (autoMapped > 0) {
      const unmappedCount = allFieldsToMap.length - autoMapped;
      let messageText = `Auto-mapped ${autoMapped} out of ${allFieldsToMap.length} fields.`;
      if (unmappedCount > 0) {
        messageText += ` ${unmappedCount} fields need manual mapping.`;
      }
      setMessage(createToastMessage(messageText, "success", 3000));
    }
  }, [allFileHeaders, productAdminFields, mandatoryFields]);
  useEffect(() => {
    if (clientIdSubmitted && fieldsFetched && allFileHeaders.length > 0 && !hasExistingMappings) {
      performAutoMapping();
    }
  }, [clientIdSubmitted, fieldsFetched, allFileHeaders, hasExistingMappings, performAutoMapping]);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasExistingMappings && e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setShowUploadButton(true);
    }
  };
  const handleCancelFile = () => {
    setFile(null);
    setFileName("");
    setShowUploadButton(false);
    const fileInput = document.getElementById("file-upload") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };
  const handleUploadClick = async () => {
    if (file) {
      await uploadFileToS3();
    }
  };
  const handleMappingChange = (field: string, selectedOption: any) => {
    const uploadedField = selectedOption ? selectedOption.value : null;
    setMapping((prev) => ({ ...prev, [field]: uploadedField }));
    if (fieldRefs.current[field]) {
      fieldRefs.current[field]?.classList.remove("highlight-error");
      const errorMessage = fieldRefs.current[field]?.querySelector(".error-message");
      if (errorMessage) {
        fieldRefs.current[field]?.removeChild(errorMessage);
      }
    }
  };
  const handleSubmit = async () => {
    if (!clientId) {
      setMessage(createToastMessage("Client ID is required.", "warning", 3000));
      return;
    }
    Object.keys(fieldRefs.current).forEach(field => {
      if (fieldRefs.current[field]) {
        fieldRefs.current[field]?.classList.remove("highlight-error");
        const errorMessage = fieldRefs.current[field]?.querySelector(".error-message");
        if (errorMessage) {
          fieldRefs.current[field]?.removeChild(errorMessage);
        }
      }
    });
    const unmappedFields = mandatoryFields.filter((field) => !mapping[field]);
    if (unmappedFields.length > 0) {
      unmappedFields.forEach((field) => {
        if (fieldRefs.current[field]) {
          const cardElement = fieldRefs.current[field];
          cardElement?.classList.add("highlight-error");
          if (!cardElement?.querySelector(".error-message")) {
            const errorMessage = document.createElement("p");
            errorMessage.textContent = "This field is required.";
            errorMessage.className = "error-message";
            cardElement?.appendChild(errorMessage);
          }
        }
      });
      const firstUnmappedField = unmappedFields[0];
      if (fieldRefs.current[firstUnmappedField]) {
        fieldRefs.current[firstUnmappedField]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setMessage(createToastMessage("Please map all mandatory fields.", "warning", 3000));
      return;
    }
    setShowSaveWarning(true);
  };
  const confirmSave = async () => {
    setShowSaveWarning(false);
    try {
      const response = await apiCall("get", "/api/client-apis", null, { clientId });
      const rawData = response.attributeToLabelMapping;
      const coreMapping: { [key: string]: string | null } = {};
      for (const [label, uploadedField] of Object.entries(mapping)) {
        const coreAttribute = rawData[label];
        if (coreAttribute) {
          coreMapping[coreAttribute] = uploadedField ?? null;
        }
      }
      const payload = { clientId, rawData, mappedData: coreMapping, headers: allFileHeaders };
      await apiCall("post", "/api/client-mappings", payload);
      setMessage(createToastMessage("Headers mapping complete! You can review your mappings once in the PROJECT DATA UPLOAD tab under 'Mapped Headers'.", "success", 3000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Error saving mappings";
      setMessage(createToastMessage(errorMessage, "error", 3000));
      setIsError(true);
    }
  };
  const handleDownloadTemplate = async () => {
    try {
      const response = await apiCall("get", "/api/bulk-upload/s3presigneddownloadurl", null, { fileName: "field_mapping_template.xlsx", action: "default" });
      const presignedUrl = response;
      if (!presignedUrl) throw new Error("Failed to retrieve presigned URL");
      const fileResponse = await axios.get(presignedUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([fileResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "field_mapping_template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setMessage(createToastMessage("Template downloaded successfully.", "success", 3000));
    } catch (error) {
      setMessage(createToastMessage("Failed to download the template.", "error", 3000));
      setIsError(true);
    }
  };
  const handleBackClick = () => { navigate(-1); };
  return (
    <div className={`initialsetup ${hasExistingMappings ? 'disabled-state ' : ''}`}>
      {!fileUploaded && (
        <>
          <div className="initialstup-container">
            <h1>Field Mapping</h1>
            {fields.view && (
              <button onClick={handleDownloadTemplate} className="download-template" disabled={hasExistingMappings}>Download Template</button>
            )}
          </div>
          <div className="upload-section">
            <h3>Step 1: Upload Your File*(File that contains Headers only)*</h3>
            {fields.upload && (
              <div className="file-upload-container">
                <label htmlFor="file-upload" className={`file-upload-label ${hasExistingMappings || showUploadButton ? 'disabled' : ''}`}>
                  <span className="file-upload-icon">📁 Choose File</span>
                </label>
                <input id="file-upload" type="file" onChange={handleFileChange} accept=".csv, .xls, .xlsx, .json" className="hidden-input" disabled={hasExistingMappings || showUploadButton} />
                {fileName && (
                  <div className="file-selected-info">
                    <span className="file-name">{fileName}</span>
                  </div>
                )}
                {showUploadButton && (
                  <div className="upload-actions">
                    <button onClick={handleUploadClick} className="upload-button">Upload File</button>
                    <button onClick={handleCancelFile} className="cancel-button">Cancel</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {(fileUploaded || hasExistingMappings) && clientIdSubmitted && fieldsFetched && (
        <div className="field-mapping-section">
          <h1>Product Admin Field Mapping</h1>
          <h3>{hasExistingMappings ? 'Existing Field Mappings' : 'Step 2: Map Product Admin Fields'}</h3>
          <div className="grid">
            {mandatoryFields
              .filter((field) => productAdminFields.includes(field))
              .map((field) => {
                const currentValue = hasExistingMappings ? existingMappedData[field] : mapping[field];
                const isAutoMapped = autoMappedFields.has(field);
                const needsManualMapping = !hasExistingMappings && !currentValue && !isAutoMapped;
                const dropdownOptions = allFileHeaders.length > 0
                  ? [...allFileHeaders]
                    .sort((a, b) => a.localeCompare(b))
                    .map((header: string) => ({ value: header, label: header }))
                  : [{ value: "", label: "No headers available" }];
                return (
                  <div key={field} className={`mapping-card ${hasExistingMappings ? 'disabled' : ''}${needsManualMapping ? 'highlight-auto-unmapped' : ''}`} ref={(el) => { fieldRefs.current[field] = el; }}>
                    <label>{mandatoryFields.includes(field) && <span className="mandatory-asterisk">*</span>}{field}:</label>
                    <Select className="Select" options={dropdownOptions} value={currentValue ? { value: currentValue, label: currentValue } : null} onChange={(selectedOption) => !hasExistingMappings && handleMappingChange(field, selectedOption)} isDisabled={hasExistingMappings} placeholder="Select column..." />
                  </div>
                );
              })}
            {productAdminFields
              .filter((field) => !mandatoryFields.includes(field))
              .map((field) => {
                const currentValue = hasExistingMappings ? existingMappedData[field] : mapping[field];
                const dropdownOptions = allFileHeaders.length > 0
                  ? [...allFileHeaders]
                    .sort((a, b) => a.localeCompare(b))
                    .map((header: string) => ({ value: header, label: header }))
                  : [{ value: "", label: "No headers available" }];
                return (
                  <div key={field} className={`mapping-card ${hasExistingMappings ? 'disabled' : ''}`}>
                    <label>{field}:</label>
                    <Select className="Select" options={dropdownOptions} value={currentValue ? { value: currentValue, label: currentValue } : null} onChange={(selectedOption) => !hasExistingMappings && handleMappingChange(field, selectedOption)} isDisabled={hasExistingMappings} placeholder="Select column..." />
                  </div>
                );
              })}
          </div>
          {fields.create && !hasExistingMappings && (
            <button onClick={handleSubmit} className="submit-button">Save & Next</button>
          )}
        </div>
      )}
      {showSaveWarning && (
        <ConfirmationPopup message="Kindly check all fields before saving. Once you save, you will get one-time edit access in the Project Data Upload tab under 'Mapped Headers'." onConfirm={confirmSave} onCancel={() => setShowSaveWarning(false)} confirmText="Continue" cancelText="Review Again" />
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      <button className="back" onClick={handleBackClick}>Back</button>
    </div>
  );
};
export default InitialSetup;